// =============================================================================
// domain/customChart.ts — the geometry of a custom widget's chart.
// -----------------------------------------------------------------------------
// Relocated from the old client's `renderCustomChart`. Everything here is
// arithmetic on a 600×180 box: which scale each series gets, where its bars and
// points land, and where the axis and endpoint labels sit after collisions are
// resolved. No SVG — the component renders what this returns.
//
// Splitting it out is not tidiness. Three of the four rules below are invisible
// in a screenshot and are the whole reason the chart is honest:
//
// ── 🚨 ONE AXIS WHEN THE UNITS MATCH, TWO WHEN THEY DO NOT ──
//
// Overlay `Avg win` and `Avg loss` — both dollars — and they share ONE
// zero-anchored axis, because the comparison is the point. Overlay `Profit
// factor` (×) with `Win rate` (%) on a shared axis and the profit factor, which
// lives between 0 and 3, is a flat line along the floor next to a number that
// reaches 100. So differing units get INDEPENDENT bands, each auto-scaled, each
// with its own colour-coded axis.
//
// ⚠ And with two mixed units the axes go left and right. With three or four
// there is nowhere to put four axes, so those fall back to shape plus the
// endpoint values — `axisMode: 'none'`. That is a deliberate degradation, not an
// omission.
//
// ── ⚠ ZERO-ANCHORED ONLY ON THE SHARED SCALE ──
//
// A shared scale keeps 0 in range un-padded, so the zero line means something
// and bars grow from it. An independent band fits its own data with 12% padding
// and bars grow from the FLOOR, because "zero" on a band that never approaches
// zero is off-screen and a bar drawn to it would be the full height of the card.
//
// ── ⚠ BARS MOVE EVERY x, INCLUDING THE LINES' ──
//
// With bars present, points sit on bucket CENTRES so a line rides over its own
// bars. Without bars, points run edge to edge. Mixing the two rules puts a line
// half a bucket off from the bars it is meant to explain.
//
// ── ⚠ LABELS ARE PUSHED APART, THEN PUSHED BACK IN ──
//
// Two series ending a point apart print two labels on top of each other. They
// are separated by a minimum gap, then the whole stack is shifted back inside
// the plot if separating them pushed it out the bottom. Order matters: separate
// first, clamp second.
// =============================================================================

import type { SeriesDef, SeriesPoint } from './customSeries';

/** The old client's viewBox and padding. */
export const CW_W = 600;
export const CW_H = 180;
const PAD_T = 12;
const PAD_B = 22;
const CH = CW_H - PAD_T - PAD_B;

/** Minimum vertical separation between two stacked labels. */
const VALUE_GAP = 12;
const AXIS_GAP = 11;

/** A single-series bar takes the P&L colours; an overlay takes the series'. */
export const BAR_POSITIVE = '#2e9dff';
export const BAR_NEGATIVE = '#f6685e';

export type AxisMode = 'single' | 'dual' | 'none';

export interface Scale {
  /** value → y, in viewBox units. */
  y: (v: number) => number;
  /** The DATA's true extent, for the axis labels — not the padded range. */
  lo: number;
  hi: number;
}

export interface BarRect {
  x: number; y: number; width: number; height: number; color: string;
}

export interface LabelItem {
  x: number; y: number; color: string; text: string; anchor: 'start' | 'end';
  /** The unit the text should be formatted with. The caller formats. */
  unit: string;
  value: number;
}

export interface XLabel { x: number; text: string; anchor: 'start' | 'middle' | 'end' }

export interface ChartGeometry {
  width: number;
  height: number;
  /** The plot's left and right edges — the zero line spans exactly these. */
  padL: number;
  padR: number;
  axisMode: AxisMode;
  /** Only meaningful on a shared, zero-anchored scale — null otherwise. */
  zeroY: number | null;
  bars: BarRect[];
  /** `key` → the polyline points, for every non-bar series. */
  lines: { key: string; color: string; points: string; area: string | null }[];
  /** Latest value at each line/area series' right edge. Empty in dual mode. */
  values: LabelItem[];
  /** Min/max of each rendered axis. */
  axes: LabelItem[];
  xLabels: XLabel[];
}

function makeScale(
  points: readonly SeriesPoint[], keys: readonly string[], anchorZero: boolean,
): Scale {
  let lo = Infinity; let hi = -Infinity;
  for (const p of points) {
    for (const k of keys) {
      const v = p.values[k] ?? 0;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  if (!Number.isFinite(lo)) { lo = 0; hi = 1; }
  const dataLo = lo; const dataHi = hi;
  let rlo = lo; let rhi = hi;
  if (anchorZero) { rlo = Math.min(0, rlo); rhi = Math.max(0, rhi); }
  if (rlo === rhi) rhi = rlo + 1;
  // ⚠ Padding on the INDEPENDENT band only. Padding a zero-anchored scale would
  // float the zero line off the baseline it is meant to be.
  if (!anchorZero) { const p = (rhi - rlo) * 0.12; rlo -= p; rhi += p; }
  const rng = rhi - rlo;
  return { y: (v) => PAD_T + CH - ((v - rlo) / rng) * CH, lo: dataLo, hi: dataHi };
}

/**
 * Separate a column of labels, then push the column back inside the plot.
 *
 * ⚠ MUTATES IN PLACE and returns the same array, matching the old client. The
 * two passes are not interchangeable: clamping first and separating second
 * re-creates the overlap it just fixed.
 */
function deCollide<T extends { y: number }>(items: T[], gap: number): T[] {
  items.sort((a, b) => a.y - b.y);
  for (let i = 1; i < items.length; i += 1) {
    const prev = items[i - 1] as T; const cur = items[i] as T;
    if (cur.y < prev.y + gap) cur.y = prev.y + gap;
  }
  const last = items[items.length - 1];
  const overflow = last ? last.y - (PAD_T + CH) : 0;
  if (overflow > 0) for (const it of items) it.y -= overflow;
  for (const it of items) it.y = Math.max(PAD_T + 5, it.y);
  return items;
}

export function buildChartGeometry(
  points: readonly SeriesPoint[], series: readonly SeriesDef[],
): ChartGeometry | null {
  const n = points.length;
  if (!n || !series.length) return null;

  const units = new Set(series.map((s) => s.unit || ''));
  const independent = series.length > 1 && units.size > 1;
  const axisMode: AxisMode = !independent
    ? 'single'
    : (series.length === 2 ? 'dual' : 'none');
  const leftAxis = axisMode === 'single' || axisMode === 'dual';
  const rightAxis = axisMode === 'dual';

  const barSeries = series.filter((s) => s.chart === 'bar');
  const hasBars = barSeries.length > 0;
  const hasLineLike = series.some((s) => s.chart !== 'bar');
  // ⚠ Endpoint values are suppressed in dual mode — the right axis is already
  // there, and the two would print on top of each other.
  const showVals = hasLineLike && axisMode !== 'dual';

  const padL = leftAxis ? 46 : 4;
  const padR = rightAxis ? 46 : (showVals ? 54 : 4);
  const cw = CW_W - padL - padR;

  const sharedY = makeScale(points, series.map((s) => s.key), true);
  const scaleOf: Record<string, Scale> = {};
  for (const s of series) {
    scaleOf[s.key] = independent ? makeScale(points, [s.key], false) : sharedY;
  }

  const groupW = cw / n;
  const edgeX = (i: number): number => (n === 1 ? padL + cw / 2 : padL + (i / (n - 1)) * cw);
  // ⚠ See the header — bars move the lines too.
  const px = (i: number): number => (hasBars ? padL + (i + 0.5) * groupW : edgeX(i));

  const zeroY = independent ? null : sharedY.y(0);
  const floor = PAD_T + CH;

  const bars: BarRect[] = [];
  if (hasBars) {
    const bw = Math.max(2, (groupW / barSeries.length) * 0.7);
    points.forEach((p, i) => {
      barSeries.forEach((s, bi) => {
        const v = p.values[s.key] ?? 0;
        const yv = (scaleOf[s.key] as Scale).y(v);
        const base = independent ? floor : sharedY.y(0);
        const x = padL + i * groupW + (groupW - bw * barSeries.length) / 2 + bi * bw;
        bars.push({
          x,
          y: Math.min(yv, base),
          width: bw * 0.85,
          height: Math.max(1, Math.abs(yv - base)),
          // ⚠ A LONE bar series is coloured by SIGN; an overlay is coloured by
          // series, because there the colour is the legend.
          color: series.length > 1 ? s.color : (v >= 0 ? BAR_POSITIVE : BAR_NEGATIVE),
        });
      });
    });
  }

  const lines = series.filter((s) => s.chart !== 'bar').map((s) => {
    const Y = (scaleOf[s.key] as Scale).y;
    const pts = points.map((p, i) => `${px(i).toFixed(1)},${Y(p.values[s.key] ?? 0).toFixed(1)}`).join(' ');
    const base = independent ? floor : sharedY.y(0);
    return {
      key: s.key,
      color: s.color,
      points: pts,
      area: s.chart === 'area'
        ? `${px(0).toFixed(1)},${base.toFixed(1)} ${pts} ${px(n - 1).toFixed(1)},${base.toFixed(1)}`
        : null,
    };
  });

  const values: LabelItem[] = [];
  if (showVals) {
    const last = points[n - 1] as SeriesPoint;
    const lx = CW_W - padR + 6;
    for (const s of series.filter((x) => x.chart !== 'bar')) {
      const v = last.values[s.key] ?? 0;
      values.push({
        x: lx, y: (scaleOf[s.key] as Scale).y(v), color: s.color, text: '',
        anchor: 'start', unit: s.unit, value: v,
      });
    }
    deCollide(values, VALUE_GAP);
  }

  const axes: LabelItem[] = [];
  const pushAxis = (sc: Scale, x: number, color: string, unit: string, anchor: 'start' | 'end'): void => {
    axes.push({ x, y: sc.y(sc.hi), color, text: '', anchor, unit, value: sc.hi });
    // ⚠ ONE label when the band is flat, not two identical ones stacked.
    if (Math.abs(sc.hi - sc.lo) > 1e-9) {
      axes.push({ x, y: sc.y(sc.lo), color, text: '', anchor, unit, value: sc.lo });
    }
  };
  if (axisMode === 'single') {
    pushAxis(sharedY, padL - 4, 'var(--text-3)', series[0]?.unit ?? '', 'end');
  } else if (axisMode === 'dual') {
    const a = series[0] as SeriesDef; const b = series[1] as SeriesDef;
    pushAxis(scaleOf[a.key] as Scale, padL - 4, a.color, a.unit, 'end');
    pushAxis(scaleOf[b.key] as Scale, CW_W - padR + 4, b.color, b.unit, 'start');
  }
  // Each side de-collides on its own — a left and a right label at the same
  // height are not a collision.
  deCollide(axes.filter((a) => a.anchor === 'end'), AXIS_GAP);
  deCollide(axes.filter((a) => a.anchor === 'start'), AXIS_GAP);

  // ⚠ First / middle / last, deduped. A label per bucket is unreadable at 90
  // days, and `[...new Set()]` is what keeps a 2-point chart from printing its
  // first label twice.
  const idxs = [...new Set(n <= 1 ? [0] : [0, Math.floor(n / 2), n - 1])];
  const xLabels: XLabel[] = idxs.map((i) => ({
    x: px(i),
    text: points[i]?.label ?? '',
    anchor: i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle',
  }));

  return {
    width: CW_W, height: CW_H, padL, padR, axisMode, zeroY,
    bars, lines, values, axes, xLabels,
  };
}
